import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import { useLocation, useNavigate, useParams } from "react-router";
import { Button, Chip, Stack, Tooltip } from "@mui/material";
import {
  BackButton,
  ConfirmNotification,
  DataGridActionsColumn,
  SelectDropdown,
  StatusDropdown,
  TableView1,
  useStatusColumn,
} from "@/components";
import { formatDateTimeShort, getRoleTypeNameFromUser } from "@/utils";
import { useLazyQuery } from "@apollo/client/react";
import {
  GET_ALL_PARTNERS,
  useHasPermission,
  useLoginAs,
  usePageTitle,
  usePartnerUsersList,
  useRemoveUserFromPartner,
  useRemoveUserFromTenant,
  useSystemUsersList,
  useTenantList,
  useTenantUsersList,
  useUpdateUser,
  useUsersList,
} from "@/hooks";
import { AddUser, ChangePassword, LinkUser } from "@/pages";
import { permissionsUsersFull } from "@/utils/permissions";
import type { DropdownOption } from "@/types";

// import DeleteModel from "./components/DeleteModel";
// import LoginModel from "./components/LoginModel";
// import UserStatusFilter from "./UserStatusFilter";
// import EditModal from "./components/EditModal";

type UserFilter = "all" | "system" | "partner" | "tenant" | "company";

interface UsersListProps {
  parentPath?: string;
  showAdd?: boolean;
  showLoginAs?: boolean;
  showLink?: boolean;
  showExport?: boolean;
  showEdit?: boolean;
  showUnlink?: boolean;
  showDelete?: boolean;
  showProfile?: boolean;
  showChangePassword?: boolean;
  showSystemRoles?: boolean;
  showCompanies?: boolean;
  showLogin?: boolean;
  showSettings?: boolean;
  showNotifications?: boolean;
  userFilter: UserFilter;
}

interface UserRow {
  id?: string;
  name?: string;
  email?: string;
  countryCode?: string;
  timezone?: string;
  status?: string;
  isVerified?: boolean;
  lastLogin?: string | Date | null;
  partnerName?: string;
  [key: string]: unknown;
}

interface DialogParameters {
  action?: string;
  id?: string;
}

type InputStatus = string | { value?: string };

const getInputStatusValue = (status: InputStatus): string => {
  if (typeof status === "string") {
    return status;
  }

  return status?.value || "";
};

export const UsersList = (props: UsersListProps): React.ReactElement => {
  const {
    parentPath = "system-admin",
    showAdd,
    showEdit,
    showCompanies,
    showChangePassword,
    showSystemRoles,
    showLogin,
    showUnlink,
    showDelete,
    showExport,
    userFilter,
    showProfile,
    showSettings = true,
    showNotifications = true,
  } = props;
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Set page title
  usePageTitle(gettext("Users"));

  // const parentPath = props.parentPath || "system-admin";
  const { user } = useUser();
  const { tenantId = user.currentTenantId, partnerId = user.currentPartnerId } =
    useParams<{
      tenantId?: string;
      partnerId?: string;
    }>();
  const roleType = getRoleTypeNameFromUser(user);

  const { allow: allowUsersFull } = useHasPermission(
    permissionsUsersFull,
    "common",
  );
  // const { allow: allowAllUsersFull } = useHasPermission(
  //   permissionsUsersFull,
  //   "common",
  // );
  // const { allow: allowPartnerUsersFull } = useHasPermission(
  //   permissionsUsersFull,
  //   "common",
  // );
  // const { allow: allowSystemUsersFull } = useHasPermission(
  //   permissionsUsersFull,
  //   "common",
  // );

  //#region useMemo
  // const partnerId = useMemo(() => {
  //   let id = "";
  //   if (user.partner.id) {
  //     id = user.partner.id;
  //   }
  //   return id;
  // }, [user.partner.id]);

  // const partnerName = useMemo(() => {
  //   let name = "";
  //   if (user.partner.name) {
  //     name = user.partner.name;
  //   }
  //   return name;
  // }, [user.partner.name]);

  const showItems = useMemo(() => {
    let returnValue: {
      showAdd: boolean;
      showEdit: boolean;
      showCompanies: boolean;
      showChangePassword: boolean;
      showSystemRoles: boolean;
      showLoginAs?: boolean;
      showLogin: boolean;
      showUnlink: boolean;
      showDelete: boolean;
      showProfile: boolean;
      showSettings: boolean;
      showNotifications: boolean;
    };

    switch (userFilter) {
      case "tenant":
        returnValue = {
          showAdd: (showAdd && allowUsersFull) ?? false,
          showEdit: (showEdit && allowUsersFull) ?? false,
          showCompanies: showCompanies ?? false,
          showChangePassword: (showChangePassword && allowUsersFull) ?? false,
          showSystemRoles: showSystemRoles ?? false,
          showLogin: showLogin ?? false,
          showUnlink: (showUnlink && allowUsersFull) ?? false,
          showDelete: (showDelete && allowUsersFull) ?? false,
          showProfile: false,
          showSettings: showSettings ?? true,
          showNotifications: showNotifications ?? true,
        };
        break;
      case "partner":
        returnValue = {
          showAdd: (showAdd && allowUsersFull) ?? false,
          showEdit: (showEdit && allowUsersFull) ?? false,
          showCompanies: showCompanies ?? false,
          showChangePassword: (showChangePassword && allowUsersFull) ?? false,
          showSystemRoles: showSystemRoles ?? false,
          showLogin: showLogin ?? false,
          showUnlink: (showUnlink && allowUsersFull) ?? false,
          showDelete: (showDelete && allowUsersFull) ?? false,
          showProfile: false,
          showSettings: showSettings ?? true,
          showNotifications: showNotifications ?? true,
        };
        break;
      case "system":
        returnValue = {
          showAdd: (showAdd && allowUsersFull) ?? false,
          showEdit: (showEdit && allowUsersFull) ?? false,
          showCompanies: showCompanies ?? false,
          showChangePassword: (showChangePassword && allowUsersFull) ?? false,
          showSystemRoles: showSystemRoles ?? false,
          showLogin: showLogin ?? false,
          showUnlink: (showUnlink && allowUsersFull) ?? false,
          showDelete: (showDelete && allowUsersFull) ?? false,
          showProfile: showProfile ?? false,
          showSettings: showSettings ?? true,
          showNotifications: showNotifications ?? true,
        };
        break;
      case "all":
        returnValue = {
          showAdd: (showAdd && allowUsersFull) ?? false,
          showEdit: (showEdit && allowUsersFull) ?? false,
          showCompanies: showCompanies ?? false,
          showChangePassword: (showChangePassword && allowUsersFull) ?? false,
          showSystemRoles: showSystemRoles ?? false,
          showLogin: showLogin ?? false,
          showUnlink: (showUnlink && allowUsersFull) ?? false,
          showDelete: (showDelete && allowUsersFull) ?? false,
          showProfile: showProfile ?? false,
          showSettings: showSettings ?? true,
          showNotifications: showNotifications ?? true,
        };
        break;
      default:
        returnValue = {
          showAdd: false,
          showEdit: false,
          showCompanies: false,
          showChangePassword: false,
          showSystemRoles: false,
          showLogin: false,
          showUnlink: false,
          showDelete: false,
          showProfile: false,
          showSettings: false,
          showNotifications: false,
        };
        break;
    }

    return returnValue;
  }, [
    allowUsersFull,
    showAdd,
    showChangePassword,
    showCompanies,
    showDelete,
    showEdit,
    showLogin,
    showSystemRoles,
    showUnlink,
    showProfile,
    showSettings,
    showNotifications,
    userFilter,
  ]);

  const showActionsColumn = useMemo(() => {
    const { showAdd, ...filteredShowItems } = showItems;
    void showAdd;
    return Object.values(filteredShowItems).some((item) => item === true);
  }, [showItems]);

  // const companyId = user.currentTeamId || "";

  // const companyName = user.currentTeam || "";
  //#endregion userMemo

  //#region data
  // const [getTenantList] = useLazyQuery(TENANTS_LIST);

  const { tenantsList } = useTenantList({
    tenantId: tenantId,
  });

  const [getPartners] = useLazyQuery(GET_ALL_PARTNERS);

  // const [getAllUsers] = useLazyQuery(GET_USERS_LIST);

  // const [usersList] = useUsersList();

  // const [getSystemUsers] = useLazyQuery(GET_SYSTEM_USERS_LIST);
  // const [
  //   getPartnerUsers,
  //   {data: partnerUsers, loading: loadingPartnerUsers},
  // ] = useLazyQuery(PARTNERS_USERS_LIST);
  // const [getCompanyUsers] = useLazyQuery(GET_COMPANY_USERS_LIST);

  // const [getTenantUsers, {data: tenantUsers, loading: loadingTenantUsers}] =
  //   useLazyQuery(TENANTS_USERS_LIST);

  const { removeUserFromTenant } = useRemoveUserFromTenant();
  const { removeUserFromPartner } = useRemoveUserFromPartner();
  const { loginAs } = useLoginAs();

  const { usersList: allUsers, loading: loadingAllUsers } = useUsersList();
  const { usersList: systemUsers, loading: loadingSystemUsers } =
    useSystemUsersList();
  const { usersList: partnerUsers, loading: loadingPartnerUsers } =
    usePartnerUsersList(partnerId);
  const { usersList: tenantUsers, loading: loadingTenantUsers } =
    useTenantUsersList(tenantId);
  //#endregion data

  //#region useState

  const [addFormOpen, setAddFormOpen] = useState(false);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [changePasswordFormOpen, setChangePasswordFormOpen] = useState(false);
  // const [usersList, setUsersList] = useState([]);
  // const [loading, setLoading] = useState(false);
  // const [title, setTitle] = useState(gettext("Users"));
  const [editRow, setEditRow] = useState<Partial<UserRow>>({});
  // const [tenantName, setTenantName] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [dialogParameters, setDialogParameters] = useState<DialogParameters>(
    {},
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [inputStatus, setInputStatus] = useState<InputStatus>("all");
  const [inputUserType, setInputUserType] = useState<string>("all");

  const { updateUser, loading: loadingUpdate } = useUpdateUser();

  // Use the reusable status column hook
  const { statusColumn, StatusMenu } = useStatusColumn({
    onStatusChange: (row, newStatus) => {
      updateUser({
        variables: {
          userId: row.id,
          email: row.email,
          status: newStatus,
        },
      });
    },
    updating: loadingUpdate,
    fieldName: "status",
  });

  const handleToggleVerified = useCallback(
    (row: UserRow) => {
      if (!row.id) return;
      const newVerifiedStatus = !row.isVerified;
      updateUser({
        variables: {
          userId: row.id,
          email: row.email,
          isVerified: newVerifiedStatus,
        },
      });
    },
    [updateUser],
  );

  // const [backLink, setBackLink] = useState("");
  // const [statusFilter, setStatusFilter] = useState("Active");
  // const [editableRow, seteditableRow] = useState({
  // });
  // const [delete_show, setdelete] = useState(false);
  // const [toastList, updateToastList] = useState([]);
  // const [edit_show, setedit] = useState(false);
  // const [chpass_show, setchpass] = useState(false);
  // const [login_show, setlogin] = useState(false);
  // const [actionColWidth, setActionColWidth] = useState(0);
  // const [actionIconCount, setActionIconCount] = useState(0);

  //#endregion useState

  //#region useEffect
  // useEffect(() => {
  //   let iconCount = 0;
  //   if (props.showChangePassword) {
  //     setActionIconCount(++iconCount);
  //   }
  //
  //   if (props.showCompanies) {
  //     setActionIconCount(++iconCount);
  //   }
  //
  //   if (props.showDelete) {
  //     setActionIconCount(++iconCount);
  //   }
  //
  //   if (props.showEdit) {
  //     setActionIconCount(++iconCount);
  //   }
  //
  //   if (props.showLogin) {
  //     setActionIconCount(++iconCount);
  //   }
  // }, [
  //   props.showChangePassword,
  //   props.showCompanies,
  //   props.showDelete,
  //   props.showEdit,
  //   props.showLogin,
  // ]);

  // useEffect(() => {
  //   if (actionIconCount > 0) {
  //     setActionColWidth(36 * actionIconCount);
  //   }
  // }, [actionIconCount]);

  const userTypeOptions = useMemo(
    () => [
      { value: "system", label: gettext("System") },
      // { value: "partner", label: gettext("Partner") },
      // { value: "company", label: gettext("Company") },
    ],
    [gettext],
  );

  const usersList = useMemo<UserRow[]>(() => {
    let returnValue: UserRow[] = [];

    switch (userFilter) {
      case "partner":
        returnValue = partnerUsers || [];
        break;
      case "tenant":
        returnValue = tenantUsers || [];
        break;
      case "system":
        returnValue = systemUsers || [];
        break;
      case "all":
        switch (inputUserType) {
          case "system":
            returnValue = systemUsers || [];
            break;
          case "partner":
            returnValue = partnerUsers || [];
            break;
          case "company":
            returnValue = tenantUsers || [];
            break;
          case "all":
          default:
            returnValue = allUsers || [];
            break;
        }
        break;
      default:
        break;
    }

    const statusValue = getInputStatusValue(inputStatus);

    if (statusValue && statusValue !== "all") {
      returnValue = returnValue.filter((user) => user.status === statusValue);
    } else {
      returnValue = returnValue.filter(
        (user) =>
          user.status !== "Deleted" && user.status?.toLowerCase() !== "deleted",
      );
    }

    return returnValue;
  }, [
    allUsers,
    inputStatus,
    inputUserType,
    partnerUsers,
    systemUsers,
    tenantUsers,
    userFilter,
  ]);

  const backLink = useMemo(() => {
    let returnValue = "";
    switch (userFilter) {
      case "partner":
        if (parentPath !== "partner-admin") {
          returnValue = `/${parentPath}/partners`;
        }
        break;
      case "tenant":
        if (parentPath !== "corporate-group") {
          returnValue = `/${parentPath}/tenants`;
        }
        break;
      case "system":
        returnValue = "";
        break;
      case "all":
        returnValue = "";
        break;
      default:
        returnValue = "";
        break;
    }

    return returnValue;
  }, [parentPath, userFilter]);

  const loading = useMemo(() => {
    return loadingAllUsers || loadingSystemUsers || loadingTenantUsers || false;
  }, [loadingAllUsers, loadingTenantUsers, loadingSystemUsers]);

  const title = useMemo(() => {
    let returnValue;
    const tenantName = tenantsList[0]?.name || "";

    switch (userFilter) {
      case "partner":
        returnValue = gettext("Partner Users for ") + partnerName;
        break;
      case "tenant":
        returnValue = gettext("Users for the corporate group: ") + tenantName;
        break;
      case "system":
        returnValue = gettext("System Users");
        break;
      case "all":
        returnValue = gettext("All Users");
        break;
      default:
        returnValue = gettext("Users");
        break;
    }

    return returnValue;
  }, [gettext, partnerName, userFilter, tenantsList]);

  // useEffect(() => {
  //   if (partnerId) {
  //     setUsersList(partnerUsers?.partnersUsersList);
  //     setBackLink(`/${parentPath}/partners`);
  //   }
  // }, [parentPath, partnerId, partnerUsers]);

  // useEffect(() => {
  //   if (tenantId) {
  //     setUsersList(tenantUsers?.tenantsUsersList);
  //     setBackLink(`/${parentPath}/tenants`);
  //   }
  // }, [parentPath, tenantId, tenantUsers]);

  // useEffect(() => {
  //   switch (props.userFilter) {
  //     case "all":
  //       // setLoading(true);
  //       // setTitle(gettext("All Users"));
  //       // getAllUsers().then((res) => {
  //       //   const resUsers = res.data.allUsers;
  //       //   if (!isEqual(usersList, resUsers)) {
  //       //     setUsersList(resUsers);
  //       //   }
  //       //   setLoading(res.loading);
  //       // });
  //
  //       // if (!isEqual(usersList, allUsers)) {
  //       //   setLoading(loadingAllUsers);
  //       //   setUsersList(allUsers);
  //       // }
  //       break;
  //     case "system":
  //       // setLoading(true);
  //       // setTitle(gettext("System Users"));
  //       // getSystemUsers().then((res) => {
  //       //   const resUsers = res.data.systemUsers;
  //       //   if (!isEqual(usersList, resUsers)) {
  //       //     setUsersList(resUsers);
  //       //   }
  //       //   setLoading(res.loading);
  //       // });
  //
  //       // if (!isEqual(usersList, systemUsers)) {
  //       //   setLoading(loadingSystemUsers);
  //       //   setUsersList(systemUsers);
  //       // }
  //       break;
  //     case "partner":
  //       // setLoading(true);
  //       // setTitle(gettext("Partner Users for ") + partnerName);
  //       // getPartnerUsers().then((res) => {
  //       //   const resUsers = res.data.partnersUsersList;
  //       //   if (!isEqual(usersList, resUsers)) {
  //       //     setUsersList(resUsers);
  //       //   }
  //       //   setLoading(res.loading);
  //       // });
  //       // getPartnerUsers({
  //       //   variables: {
  //       //     partnerId,
  //       //   },
  //       // }).then();
  //
  //       // if (!isEqual(usersList, partnerUsers)) {
  //       //   setLoading(loadingPartnerUsers);
  //       //   setUsersList(partnerUsers);
  //       // }
  //       break;
  //     case "company":
  //       // setLoading(true);
  //       // setTitle(gettext("Company Users for ") + companyName);
  //       // getCompanyUsers({
  //       //   variables: {
  //       //     companyId,
  //       //   },
  //       // }).then((res) => {
  //       //   const resUsers = res.data.companyUsers;
  //       //   if (!isEqual(usersList, resUsers)) {
  //       //     setUsersList(resUsers);
  //       //   }
  //       //   setLoading(res.loading);
  //       // });
  //
  //       // if (!isEqual(usersList, companyUsers)) {
  //       //   setLoading(loadingCompanyUsers);
  //       //   setUsersList(companyUsers);
  //       // }
  //       break;
  //     case "tenant":
  //       // setLoading(true);
  //       // setTitle(gettext("Users for the business: ") + tenantName);
  //       // getTenantUsers({
  //       //   variables: {
  //       //     tenantId,
  //       //   },
  //       // }).then();
  //
  //       // if (!isEqual(usersList, companyUsers)) {
  //       //   setLoading(loadingCompanyUsers);
  //       //   setUsersList(companyUsers);
  //       // }
  //       break;
  //     default:
  //       // setUsersList([]);
  //       break;
  //   }
  // }, [
  //   companyId,
  //   companyName,
  //   getAllUsers,
  //   getCompanyUsers,
  //   getPartnerUsers,
  //   getSystemUsers,
  //   getTenantUsers,
  //   gettext,
  //   partnerId,
  //   partnerName,
  //   props.userFilter,
  //   tenantId,
  //   tenantName,
  //   usersList,
  // ]);

  // useEffect(() => {
  //   if (tenantId) {
  //     getTenantList({
  //       variables: {
  //         tenantId: tenantId,
  //       },
  //     }).then((res) => {
  //       const tName = res?.data?.tenantsList[0]?.name;
  //       if (tName && tName !== tenantName) {
  //         setTenantName(tName);
  //       }
  //     });
  //   }
  // }, [getTenantList, tenantId, tenantName]);

  useEffect(() => {
    if (partnerId) {
      getPartners({
        variables: {
          partnerId,
        },
      }).then((res: any) => {
        const pName = res?.data?.getPartners[0]?.name;
        if (pName && pName !== partnerName) {
          setPartnerName(pName);
        }
      });
    }
  }, [getPartners, partnerId, partnerName]);

  //#endregion useEffect

  // const { searchString } = props;
  // const handleClose_delete = () => setdelete(false);
  // const handleShow_delete = () => setdelete(true);
  // const handleOpen = () => setAddModalOpen(true);
  // const handleClose = () => setAddModalOpen(false);
  // const handleClose_edit = () => setedit(false);
  // const handleShow_edit = () => setedit(true);
  // const handleClose_login = () => setlogin(false);
  // const { usersList, loading } = useUsersList(searchString, statusFilter);
  // const handleClose_chpass = message => {
  //   if (message === "success") {
  //     updateToastList([
  //       {
  //         title: "Success",
  //         description: "Password updated successfully",
  //         type: "success"
  //       }
  //     ]);
  //   }
  //   setchpass(false);
  // };
  // const handleShow_chpass = () => setchpass(true);
  // const handleShow_login = () => setlogin(true);
  // function edit_functionality(row) {
  //   seteditableRow(row);
  //
  //   handleShow_edit();
  // }

  // function chpass_functionality(row) {
  //   seteditableRow(row);
  //
  //   handleShow_chpass();
  // }

  // function login_functionality() {
  //   // row
  //   // seteditableRow(row);
  //   // handleShow_login();
  // }

  // function delete_functionality() {
  //   // row
  //   // seteditableRow(row);
  //   // handleShow_delete();
  // }

  /**
   *
   * @param row
   */
  // function roles_functionality(row) {
  //   navigate(`/system-admin/users/${row.id}/update-role`);
  // }

  /**
   *
   * @param row
   */
  // function customers_functionality(row) {
  //   navigate(`${location.pathname}/${row.id}/customers`, {
  //     state: {
  //       user: row,
  //     },
  //   });
  // }

  /**
   *
   * @param row
   * @param action
   */

  /**
   *
   * @param row
   * @param action
   */
  // function performAction(row, action) {
  //   switch (action) {
  //     case "edit":
  //       handleEdit(row);
  //       break;
  //     case "delete":
  //       delete_functionality(row);
  //       break;
  //     case "roles":
  //       roles_functionality(row);
  //       break;
  //     case "customers":
  //       customers_functionality(row);
  //       break;
  //     case "change-password":
  //       handleChangePassword(row);
  //       // chpass_functionality(row);
  //       break;
  //     case "login":
  //       login_functionality(row);
  //       break;
  //     default:
  //       break;
  //   }
  // }

  /**
   *
   * @param action
   * @param row
   */
  // function columnFormatter(params) {
  //   return (
  //     <div className="w-100 d-flex justify-content-center">
  //       {!!props.showEdit && (
  //         <Tooltip title={gettext("Edit")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "edit")}>
  //             <FaUserEdit size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {!!props.showDeleteSingle && (
  //         <Tooltip title={gettext("Delete")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "delete")}>
  //             <MdDelete size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {!!props.showChangePassword && (
  //         <Tooltip title={gettext("Change Password")} arrow>
  //           <IconButton
  //             onClick={() => performAction(params.row, "change-password")}
  //           >
  //             <MdPassword size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {/* {!restricted && ( */}
  //       {/*   <Tooltip title={gettext("Update Roles")} arrow> */}
  //       {/*     <IconButton onClick={() => performAction(params.row, "roles")}> */}
  //       {/*       <IoKeySharp size={16} /> */}
  //       {/*     </IconButton> */}
  //       {/*   </Tooltip> */}
  //       {/* )} */}
  //
  //       {!!props.showCompanies && (
  //         <Tooltip title={gettext("Companies")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "customers")}>
  //             <HiMiniBuildingOffice2 size={16} />
  //             {/*<IoPersonSharp size={16} />*/}
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //
  //       {!!props.showLoginAs && (
  //         <Tooltip title={gettext("Login as")} arrow>
  //           <IconButton onClick={() => performAction(params.row, "login")}>
  //             <MdLogin size={16} />
  //           </IconButton>
  //         </Tooltip>
  //       )}
  //     </div>
  //   );
  // }

  const menuClick = (action: string, row: UserRow) => {
    switch (action) {
      case "user-edit":
        handleEdit(row);
        break;
      case "user-profile":
        navigate(`${location.pathname}/${row.id}/profile`, {
          state: {
            user: row,
          },
        });
        break;
      case "user-settings":
        navigate(`${location.pathname}/${row.id}/settings`, {
          state: {
            user: row,
          },
        });
        break;
      case "user-notifications":
        navigate(`${location.pathname}/${row.id}/notifications`, {
          state: {
            user: row,
          },
        });
        break;
      case "user-password":
        handleChangePassword(row);
        break;
      case "user-company":
        navigate(`${location.pathname}/${row.id}/customers`, {
          state: {
            user: row,
          },
        });
        break;
      case "user-system-role":
        navigate(`${location.pathname}/${row.id}/system-roles`, {
          state: {
            user: row,
          },
        });
        break;
      case "user-login":
        loginAs(row?.email);
        break;
      case "user-unlink":
        setConfirmMessage(gettext("Do you want to unlink the user?"));
        handleOnClickRemoveLink(action, row?.email);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
      field: "id",
      headerName: gettext("ID"),
      checkboxSelection: true,
      flex: 1, // Adjust this flex value as needed
    },
    {
      field: "name",
      headerName: gettext("Name"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    {
      field: "email",
      headerName: gettext("Email"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    // {
    //   field: "roles",
    //   headerName: gettext("Role"),
    //   headerAlign: "left",
    //   type: "string",
    //   flex: 1
    // },
    {
      field: "countryCode",
      headerName: gettext("Country"),
      headerAlign: "left",
      type: "string",
      maxWidth: 100,
      // flex: 1
    },
    {
      field: "timezone",
      headerName: gettext("Time Zone"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    ...(roleType !== "company" ? [statusColumn] : []),
    {
      field: "isVerified",
      headerName: gettext("Email Verified"),
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      maxWidth: 180,
      renderCell: (params: { row: UserRow }) => {
        const isVerified = Boolean(params.row?.isVerified);
        return (
          <Tooltip title={gettext("Click to toggle verification status")} arrow>
            <Chip
              size="small"
              label={isVerified ? gettext("Verified") : gettext("Unverified")}
              color={isVerified ? "success" : "default"}
              variant={isVerified ? "filled" : "outlined"}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleVerified(params.row);
              }}
              disabled={loadingUpdate}
              sx={{
                cursor: "pointer",
                fontWeight: 500,
                "&:hover": {
                  opacity: 0.85,
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "lastLogin",
      headerName: gettext("Last Login"),
      headerAlign: "left",
      minWidth: 175,
      maxWidth: 200,
      type: "date",
      valueGetter: (value: unknown) => {
        let returnValue: Date | null = null;
        if (value instanceof Date) {
          returnValue = new Date(value);
        } else if (typeof value === "string" || typeof value === "number") {
          returnValue = new Date(value);
        }
        return returnValue;
      },
      valueFormatter: (value: unknown) => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
          return gettext("Never Login");
        } else {
          return formatDateTimeShort(value);
        }
      },
    },
    {
      field: "partnerName",
      headerName: gettext("Partner"),
      headerAlign: "left",
      type: "string",
      flex: 1,
    },
    ...DataGridActionsColumn(showActionsColumn, "user", showItems, menuClick),
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    columns: {
      columnVisibilityModel: {
        id: false,
        name: true,
        email: true,
        role: false,
        country: false,
        timezone: false,
        status: true,
        isVerified: true,
        partnerName: false,
      },
    },
  };
  // const handleGetSelectedValues = (selectedValues) => {
  //
  // };
  const preToolbar = () => {
    return (
      <>
        {/* <Button variant="contained" component={Link} to={"/data/deals/add"}> */}
        {/*   {gettext("Add contract")} */}
        {/* </Button> */}
        {/* <Button disabled={disabledDeleteButton} variant="contained" color={"error"}> */}
        {/*   Delete */}
        {/* </Button> */}
      </>
    );
  };

  const handleStatusChange = (event: InputStatus) => {
    setInputStatus(event);
  };

  const handleUserTypeChange = (value: DropdownOption) => {
    const stringValue = String(value?.value ?? "all");
    setInputUserType(stringValue || "all");
  };

  const postToolbar = () => {
    const showPostToolbar =
      showAdd || userFilter === "tenant" || userFilter === "partner";
    return (
      <>
        {showPostToolbar && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ pl: 1, alignItems: "center" }}
          >
            <StatusDropdown
              onChange={handleStatusChange}
              defaultValue={getInputStatusValue(inputStatus)}
              showAll
              required
              visualStyle="autoComplete"
            />

            {userFilter === "all" && (
              <SelectDropdown
                options={userTypeOptions}
                componentLabel={gettext("User Type")}
                defaultValue={inputUserType}
                showAll
                onChange={handleUserTypeChange}
                visualStyle="autoComplete"
                required
              />
            )}

            {backLink && <BackButton link={`${backLink}`} />}

            {showAdd && (
              <Button
                variant="contained"
                onClick={() => {
                  setAddFormOpen(true);
                }}
                sx={{
                  ml: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {gettext("Add User")}
              </Button>
            )}

            {userFilter === "tenant" && (
              <Button
                variant="contained"
                onClick={() => {
                  setLinkFormOpen(true);
                }}
                sx={{
                  ml: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {gettext("Link User")}
              </Button>
            )}

            {userFilter === "partner" && (
              <Button
                variant="contained"
                onClick={() => {
                  setLinkFormOpen(true);
                }}
                sx={{
                  ml: 1,
                }}
              >
                {gettext("Link User")}
              </Button>
            )}
          </Stack>
        )}
      </>
    );
  };

  const handleCloseAddForm = () => {
    setAddFormOpen(false);
    setEditRow({});
  };

  const handleCloseLinkForm = () => {
    setLinkFormOpen(false);
  };

  const handleCloseChangePasswordForm = () => {
    setChangePasswordFormOpen(false);
    setEditRow({});
  };

  const handleEdit = (row: UserRow) => {
    setEditRow({
      ...row,
      tenantId,
      partnerId,
    });
    setAddFormOpen(true);
  };

  const handleChangePassword = (row: UserRow) => {
    setEditRow(row);
    setChangePasswordFormOpen(true);
  };

  const handleOnClickRemoveLink = (action: string, id?: string) => {
    setDialogParameters({
      action,
      id,
    });
    setShowConfirm(true);
  };

  const handleGetResult = (obj: {
    result?: string;
    parameters?: DialogParameters;
  }) => {
    if (obj.result === "yes") {
      switch (obj?.parameters?.action) {
        case "user-unlink":
          if (tenantId) {
            removeUserFromTenant({
              tenantId,
              email: obj?.parameters?.id,
            });
          }
          if (partnerId) {
            removeUserFromPartner({
              partnerId,
              email: obj?.parameters?.id,
            });
          }
          break;
        default:
          break;
      }
    }
    setShowConfirm(false);
  };

  return (
    <>
      <TableView1
        title={title}
        rows={usersList}
        columns={columns}
        loading={loading || loadingTenantUsers || loadingPartnerUsers}
        initialState={initialState}
        preToolbar={preToolbar()}
        postToolbar={postToolbar()}
        showGridToolbar
        showGridToolbarExport={!!showExport}
        showGridToolbarQuickFilter
      />
      <AddUser
        open={addFormOpen}
        handleClose={handleCloseAddForm}
        row={editRow}
      />
      <ChangePassword
        open={changePasswordFormOpen}
        handleClose={handleCloseChangePasswordForm}
        row={editRow}
      />
      <LinkUser
        open={linkFormOpen}
        handleClose={handleCloseLinkForm}
        row={{
          partnerId,
          tenantId,
          userFilter,
        }}
      />

      <ConfirmNotification
        isOpen={showConfirm}
        parameters={dialogParameters}
        getResult={handleGetResult}
        message={confirmMessage}
      />

      <StatusMenu />
    </>
  );
};
